// The expense window (log or edit a cost), used by Am I profitable? (JourneyMoney.js) and the Log
// window. The Journey's old Money page lived here.
import { useEffect, useState } from 'react';
import { api } from './api';
import { CATEGORIES, CATEGORY_NAMES, FIRMS, firmOf, isProp } from './business';
import { dayKey } from './pnl';
import { expenseScope, hasBothKinds } from './journey';
import { ExpenseSide, sideAccounts, sideOf, useJourney, withSide } from './JourneyStory';
import { useI18n } from './i18n';

export function ExpenseDialog({ expense, accounts: shown, onClose, onSaved, onSessionExpired }) {
  const { t } = useI18n();
  // Every account, not just the side the Journey's switch shows, so a cost can go on either side.
  const { scope, allAccounts } = useJourney();
  const accounts = allAccounts.length ? allAccounts : shown;
  const [form, setForm] = useState(() => ({
    amount: expense.amount || '', date: expense.date || dayKey(new Date()), category: expense.category || 'evaluation',
    firm: expense.firm || '', accountId: expense.accountId || '', note: expense.note || '', repeat: expense.repeat || '', until: expense.until || '',
    side: expense.side || (expense._id || expense.accountId ? expenseScope(expense, new Map(accounts.map((a) => [String(a._id), a]))) : scope === 'personal' ? 'personal' : 'prop'),
  }));
  const both = hasBothKinds(accounts);
  // The side follows the category until the trader picks one (or it's an expense being edited).
  const [sidePicked, setSidePicked] = useState(Boolean(expense._id || expense.accountId || expense.side));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const props = both ? sideAccounts(accounts, form.side) : accounts.filter(isProp);
  const firmNames = [...new Set([...FIRMS.map(([n]) => n), ...accounts.map(firmOf).filter(Boolean)])].sort();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target.checked ? 'monthly' : '') : e.target.value }));

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { ...form, firm: form.firm || firmOf(props.find((a) => a._id === form.accountId)), side: both ? form.side : undefined };
      const saved = await api(expense._id ? `/expenses/${expense._id}` : '/expenses', { method: expense._id ? 'PUT' : 'POST', body });
      onSaved(saved);
    } catch (err) {
      if (err.status === 401) onSessionExpired();
      else setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal biz-dialog" onSubmit={save} role="dialog" aria-modal="true" aria-labelledby="biz-dialog-title">
        <div className="modal-header">
          <h2 id="biz-dialog-title">{expense._id ? t('Edit expense') : t('Log an expense')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        {both && <ExpenseSide value={form.side} onChange={(side) => { setSidePicked(true); setForm(withSide(side)); }} />}
        <div className="biz-fields">
          <label>{t('What it was for')}
            <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value, ...(sidePicked || !both || f.side === sideOf(e.target.value) ? {} : { side: sideOf(e.target.value), accountId: '' }) }))}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{t(CATEGORY_NAMES[c])}</option>)}
            </select>
          </label>
          <label>{t('Amount')}
            <input type="number" inputMode="decimal" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} required autoFocus placeholder="49.00" />
          </label>
          <label>{form.repeat ? t('First payment') : t('Date paid')}
            <input type="date" value={form.date} onChange={set('date')} required />
          </label>
          {props.length > 0 && (
            <label>{t('Account (optional)')}
              <select value={form.accountId} onChange={set('accountId')}>
                <option value="">{t('Not tied to one account')}</option>
                {props.map((a) => <option key={a._id} value={a._id}>{a.name}{a.attemptOf || a.archived ? ` (${t('ended')})` : ''}</option>)}
              </select>
            </label>
          )}
          <label>{t('Firm or company')}
            <input list="biz-firms" value={form.firm} onChange={set('firm')} maxLength={40} placeholder={firmOf(props.find((a) => a._id === form.accountId)) || t(form.side === 'personal' ? 'e.g. Bookmap, CME data, TradingView' : 'e.g. Topstep, Tradovate, TradingView')} />
            <datalist id="biz-firms">{firmNames.map((n) => <option key={n} value={n} />)}</datalist>
          </label>
          <label>{t('Note (optional)')}
            <input value={form.note} onChange={set('note')} maxLength={200} placeholder={t('e.g. 50K evaluation, discount code')} />
          </label>
        </div>
        <label className="biz-repeat">
          <input type="checkbox" checked={form.repeat === 'monthly'} onChange={set('repeat')} />
          {t('Charged every month')}
        </label>
        {form.repeat === 'monthly' && (
          <label className="biz-until">{t('Last month charged (leave empty if still paying)')}
            <input type="date" value={form.until} min={form.date} onChange={set('until')} />
          </label>
        )}
        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={onClose}>{t('Cancel')}</button>
          <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Save')}</button>
        </div>
      </form>
    </div>
  );
}
